import { useSyncExternalStore } from 'react';
import { env } from '@/shared/config/env';

/**
 * The logo management uploaded in Settings, for the page itself. Request of
 * 6 October 2026.
 *
 * The API keeps each upload under a version; the manifest names it. That one
 * version is held here so the sign-in card, the menu and the browser tab all
 * show the same picture as the installed app. Null means nothing was uploaded,
 * and the built-in mark stands.
 */
let version: string | null = null;
/** False until the manifest has answered: until then null means not known, not none. */
let known = false;
const listeners = new Set<() => void>();

/** The upload now in force, or null for the built-in mark. */
export function appIconVersion(): string | null {
  return version;
}

export function setAppIconVersion(next: string | null): void {
  if (known && next === version) return;
  known = true;
  version = next;
  listeners.forEach((listener) => listener());
}

export function subscribeAppIcon(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The upload in force: undefined until it is known, null for the built-in icon. */
export function useAppIconVersion(): string | null | undefined {
  const read = () => (known ? version : undefined);
  return useSyncExternalStore(subscribeAppIcon, read, read);
}

/** Forgets what was learnt. For tests. */
export function resetAppIcon(): void {
  known = false;
  version = null;
  listeners.forEach((listener) => listener());
}

/** Where a file of the uploaded set is served from. */
export function appIconUrl(iconVersion: string, file: string): string {
  return `${env.apiBaseUrl}/pwa/icons/${iconVersion}/${file}`;
}

/** The uploaded logo's address, or null while the built-in mark stands. */
export function useAppLogoUrl(): string | null {
  const current = useSyncExternalStore(subscribeAppIcon, appIconVersion, appIconVersion);
  return current === null ? null : appIconUrl(current, 'icon-192.png');
}
